import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

interface PublicKeyResponse {
  publicKeyBase64: string;
  algorithm: string;
  keySize: number;
  instructions?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RsaEncryptionService {
  private apiUrl = `${environment.apiUrl}/admin/email-config`;
  private publicKey: CryptoKey | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Carrega a chave pública RSA do servidor
   */
  loadPublicKey(): Observable<void> {
    return this.http.get<PublicKeyResponse>(`${this.apiUrl}/public-key`).pipe(
      switchMap((data) => {
        const publicKeyBase64 = data.publicKeyBase64;

        // Converter Base64 para ArrayBuffer
        const publicKeyDer = Uint8Array.from(
          atob(publicKeyBase64),
          c => c.charCodeAt(0)
        );

        // Importar chave pública
        return from(
          crypto.subtle.importKey(
            'spki',
            publicKeyDer,
            {
              name: 'RSA-OAEP',
              hash: 'SHA-256'
            },
            false,
            ['encrypt']
          )
        ).pipe(
          map((key) => {
            this.publicKey = key;
          })
        );
      })
    );
  }

  /**
   * Criptografa um texto usando a chave pública RSA
   */
  encrypt(plaintext: string): Observable<string> {
    if (!this.publicKey) {
      // Carregar chave se ainda não foi carregada
      return this.loadPublicKey().pipe(
        switchMap(() => this.encrypt(plaintext))
      );
    }

    // Converter texto para ArrayBuffer
    const plaintextBuffer = new TextEncoder().encode(plaintext);

    // Criptografar
    return from(
      crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP'
        },
        this.publicKey!,
        plaintextBuffer
      )
    ).pipe(
      map((encryptedBuffer) => {
        // Converter para Base64
        const encryptedArray = new Uint8Array(encryptedBuffer);
        const encryptedBase64 = btoa(
          String.fromCharCode(...encryptedArray)
        );

        return encryptedBase64;
      })
    );
  }

  /**
   * Criptografa múltiplos textos em paralelo
   */
  encryptMultiple(texts: string[]): Observable<string[]> {
    if (!this.publicKey) {
      // Carregar chave se ainda não foi carregada
      return this.loadPublicKey().pipe(
        switchMap(() => this.encryptMultiple(texts))
      );
    }

    // Criptografar todos os textos em paralelo usando forkJoin
    const encryptObservables = texts.map(text => this.encrypt(text));
    
    if (encryptObservables.length === 0) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    // Usar forkJoin para executar em paralelo
    return forkJoin(encryptObservables);
  }
}
